import { until } from "@vueuse/core";

import { debounce } from "lodash-es";
import { defineStore, storeToRefs } from "pinia";
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { headerManager } from "@/lib/requests";
import { loadIdentityHint, saveIdentityHint } from "@/lib/swr-cache";
import {
    AuthStatus,
    useAuthenticationManager,
} from "@/stores/authentication-store";
import {
    Organization,
    useOrganizationsManager,
} from "@/stores/organizations-store";
import { useReleaseStore } from "@/stores/release-store";

import { useUnauthorizedAccessDialog } from "./unauthorized-access-dialog";

export const useOrganizationStore = defineStore("organization", () => {
    const route = useRoute();
    const router = useRouter();
    const { authStatus, authUser } = useAuthenticationManager();
    const { isLoading: isLoadingOrganizations, organizations } =
        useOrganizationsManager();
    const { open: openUnauthorizedDialog } = useUnauthorizedAccessDialog();

    const showSelectOrganization = ref(false);
    const organization = ref<Organization>();

    async function selectOrganization() {
        // The initial navigation only completes once the lazy route component
        // has loaded, but route MATCHING is synchronous — resolve the URL
        // directly while the router hasn't settled yet (START_LOCATION has no
        // name), so org resolution (and everything gated on it: the steks
        // fetch, the org-scoped /user/me) doesn't wait for the route chunk.
        // Params and meta are exactly what the navigation will produce.
        const target =
            route.name === undefined
                ? router.resolve(
                      window.location.pathname +
                          window.location.search +
                          window.location.hash
                  )
                : route;

        if (!target.meta.requiresOrg) {
            organization.value = undefined;
            return;
        }

        const orgNameParam = target.params.orgName as string;

        // Treat the selection as settled only while the selected object's
        // identity still exists in the latest list — a hydrated
        // (stale-while-revalidate) object whose identity went stale must fall
        // through to a full re-selection once fresh data lands.
        const stillListed =
            organization.value === undefined ||
            organizations.value === undefined ||
            organizations.value.some(
                (org) => org.identity === organization.value!.identity
            );
        if (
            organization.value &&
            stillListed &&
            (organization.value.name === orgNameParam ||
                organization.value.identity === orgNameParam)
        ) {
            // Re-point at the fresh object (same identity) so fields stay
            // current after the revalidating fetch replaces the list.
            const fresh = organizations.value?.find(
                (org) => org.identity === organization.value!.identity
            );
            if (fresh && fresh !== organization.value) {
                organization.value = fresh;
            }
            return;
        }

        // A previously-loaded list is good enough to resolve from — only wait
        // for the in-flight fetch when there is no list at all. While waiting,
        // a cached identity hint (bare nanoid keyed by a hash of the org name —
        // see lib/swr-cache.ts) lets the org context resolve immediately so
        // the page's request burst isn't gated on this round-trip; the find
        // below replaces the partial hint object once the real list lands.
        if (!organizations.value?.length) {
            if (!organization.value && orgNameParam) {
                const hinted = await loadIdentityHint(
                    authUser.value?.email,
                    "org",
                    orgNameParam
                );
                if (
                    hinted &&
                    !organization.value &&
                    !organizations.value?.length
                ) {
                    organization.value = {
                        identity: hinted,
                        name: orgNameParam,
                        // Partial by design: only identity (headers) and name
                        // (display/redirects, taken from the URL) are needed
                        // before the real object replaces this.
                    } as Organization;
                }
            }
            try {
                await until(isLoadingOrganizations).not.toBeTruthy();
            } catch {
                return;
            }
        }

        if (!organizations.value) {
            return;
        }

        organization.value = organizations.value.find(
            (org) => org.name === orgNameParam || org.identity === orgNameParam
        );

        if (organization.value) {
            // Authoritative resolution from the server list — remember the
            // name -> identity mapping for the next boot.
            saveIdentityHint(
                authUser.value?.email,
                "org",
                organization.value.name,
                organization.value.identity
            );
        }

        if (!organization.value) {
            if (orgNameParam) {
                openUnauthorizedDialog({
                    status: 403,
                    message: `Organization "${orgNameParam}" was not found or you do not have access to it.`,
                });
                return;
            } else {
                if (organizations.value.length === 0) {
                    showSelectOrganization.value = true;
                    return;
                } else if (organizations.value.length === 1) {
                    organization.value = organizations.value[0];
                } else {
                    showSelectOrganization.value = true;
                    return;
                }
            }
        }

        // When orgName is undefined while an organization is selected, update
        // the url to include the selected organization. (this can happen when
        // there is only one organization, in which case the user won't be
        // shown the organization picker where clicking a stek updates the
        // url.)
        if (!orgNameParam && !target.meta.preventRedirect) {
            router.replace({
                name: "organizationHome",
                params: {
                    orgName: organization.value.name,
                },
            });
        }

        showSelectOrganization.value = false;
    }

    // wait=0 still coalesces the same-tick trigger storm from the watchers
    // below into one run, without adding a flat 100ms to every boot/org
    // switch (the old wait gated the entire page request burst).
    const debouncedSelectOrganization = debounce(selectOrganization, 0);

    // Set the header for the WebComponents requests lib.
    watch(
        () => organization.value?.identity,
        (newIdentity) => {
            if (newIdentity) {
                headerManager.updateHeaders({
                    organization: newIdentity,
                });
            }
        },
        { immediate: true }
    );

    // Reset release state when switching organizations so the "Viewing release xxx"
    // banner does not persist into the newly selected org/stek.
    watch(
        () => organization.value?.identity,
        (newIdentity, oldIdentity) => {
            if (oldIdentity !== undefined && newIdentity !== oldIdentity) {
                useReleaseStore().resetForNewStek();
            }
        }
    );

    watch(
        authStatus,
        async (newAuthStatus) => {
            if (newAuthStatus !== AuthStatus.SIGNED_IN) {
                organization.value = undefined;
            } else {
                await debouncedSelectOrganization();
            }
        },
        { immediate: true }
    );

    watch(
        () => route.params.orgName,
        (newOrgName, oldOrgName) => {
            if (newOrgName !== oldOrgName) {
                debouncedSelectOrganization();
            }
        },
        { immediate: true }
    );

    watch(
        () => route.meta.requiresOrg,
        (newRequiresOrg, oldRequiresOrg) => {
            if (!newRequiresOrg) {
                organization.value = undefined;
            } else if (newRequiresOrg !== oldRequiresOrg) {
                debouncedSelectOrganization();
            }
        },
        { immediate: true }
    );

    watch(
        organizations,
        (newOrgs) => {
            if (!newOrgs?.length) {
                organization.value = undefined;
                return;
            }
            debouncedSelectOrganization();
        },
        { deep: true, immediate: true }
    );

    return {
        showSelectOrganization,
        isLoadingOrganizations,
        organization,
    };
});

export const useOrganizationManager = () => storeToRefs(useOrganizationStore());
